/**
 * CommentsSection Component
 * Displays standard comments, operator notes, and AI analysis
 */
import { useTranslation } from "react-i18next";

import { useSettingsStore } from "../../../stores/settingsStore";

interface CommentsSectionProps {
  operatorNotes: string;
  setOperatorNotes: (notes: string) => void;
  standardComments?: string[];
  aiPoweredAnalysis?: string[];
}

export default function CommentsSection({
  operatorNotes: _operatorNotes,
  setOperatorNotes: _setOperatorNotes,
  standardComments = [],
  aiPoweredAnalysis = [],
}: CommentsSectionProps) {
  const { t } = useTranslation();
  const darkMode = useSettingsStore((state) => state.darkMode);

  return (
    <div
      className={`rounded-3xl p-4 sm:p-6 border ${
        darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
      }`}
    >
      <h3
        className={`text-base sm:text-lg font-bold mb-6 ${darkMode ? "text-white" : "text-gray-900"}`}
      >
        {t("reports.commentsOperationsNotes")}
      </h3>

      {/* Standard Comments */}
      <div className="mb-6">
        <h4
          className={`text-sm font-bold mb-3 ${darkMode ? "text-white" : "text-gray-900"}`}
        >
          {t("reports.standardComments")}
        </h4>
        {standardComments.length > 0 ? (
          <ul
            className={`space-y-2 text-sm ${darkMode ? "text-gray-300" : "text-gray-700"}`}
          >
            {standardComments.map((comment, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className={darkMode ? "text-gray-500" : "text-gray-400"}>
                  •
                </span>
                <span>{comment}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p
            className={`text-sm ${darkMode ? "text-gray-500" : "text-gray-400"}`}
          >
            {t("reports.noStandardComments") ||
              "No standard comments available."}
          </p>
        )}
      </div>

      {/* Operator Notes Area — hidden */}

      {/* AI Detailed Analysis */}
      <div>
        <h4
          className={`text-sm font-bold mb-3 ${darkMode ? "text-white" : "text-gray-900"}`}
        >
          {t("reports.aiDetailedAnalysis")}
        </h4>
        {aiPoweredAnalysis.length > 0 ? (
          <ul
            className={`space-y-2 text-sm ${darkMode ? "text-gray-300" : "text-gray-700"}`}
          >
            {aiPoweredAnalysis.map((note, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className={darkMode ? "text-gray-500" : "text-gray-400"}>
                  •
                </span>
                <span>{note}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p
            className={`text-sm ${darkMode ? "text-gray-500" : "text-gray-400"}`}
          >
            {t("reports.noAiAnalysis") || "No AI analysis available."}
          </p>
        )}
      </div>
    </div>
  );
}
